import { useI18n } from 'vue-i18n'

/**
 * Number formatting for the calculator, all through vue-i18n so separators and
 * currency placement follow the visitor's language.
 */
export function useFormat() {
  const { t, n } = useI18n()

  /**
   * USD with enough precision to compare: a pod can cost a fraction of a cent an
   * hour, so small amounts keep three significant digits instead of rounding to $0.00.
   */
  function price(value: number): string {
    if (!Number.isFinite(value)) return t('units.none')
    const options: Intl.NumberFormatOptions =
      value >= 100
        ? { maximumFractionDigits: 0 }
        : value >= 1
          ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          : { minimumFractionDigits: 2, maximumSignificantDigits: 3 }
    return n(value, { style: 'currency', currency: 'USD', ...options })
  }

  /** Millicores as vCPU: `3.92 vCPU`. */
  const cpu = (millicores: number): string =>
    t('units.vcpu', { value: n(millicores / 1000, { maximumFractionDigits: 2 }) })

  /** MiB as GiB from 1 GiB up, MiB below. */
  const memory = (mib: number): string =>
    mib >= 1024
      ? t('units.gib', { value: n(mib / 1024, { maximumFractionDigits: 2 }) })
      : t('units.mib', { value: n(mib, { maximumFractionDigits: 0 }) })

  const percent = (ratio: number): string =>
    n(ratio, { style: 'percent', maximumFractionDigits: 0 })

  const count = (value: number): string => n(value, { maximumFractionDigits: 0 })

  return { price, cpu, memory, percent, count }
}
